import React, { useEffect, useRef, useState } from "react";
import { CameraIcon, Plus } from "./icons";
import styles from "../publicvehicle.module.css";
import { formatUsPhone } from "../publicVehicleApi";
import { compressImage } from "../imageCompress";

const MAX_PHOTO_BYTES = 5 * 1024 * 1024;
const VALID_EXTENSIONS = ["jpg", "jpeg", "png"];
const VALID_TYPES = ["image/jpeg", "image/png"];
const COLORS = [
  { id: "Black", hex: "#1B1B1F" },
  { id: "White", hex: "#FFFFFF" },
  { id: "Silver", hex: "#C8CBD0" },
  { id: "Gray", hex: "#7A7D85" },
  { id: "Blue", hex: "#1F4FD1" },
  { id: "Red", hex: "#C62828" },
  { id: "Green", hex: "#2E7D4F" },
];

// The color text box is the value; a tile is highlighted when the text matches it
const isColor = (value, id) => value.trim().toLowerCase() === id.toLowerCase();

/*
 * Vehicle number / make / model / color plus photos, used by the tenant and guest pages.
 * requirePhotos: at least minPhotos are needed (new vehicles, guests). For updates photos are optional, but if
 * any are added there must be at least minPhotos (they replace the current ones).
 * existingPhotos ([{ id, url, thumb }], updates only): the vehicle's current photos, shown first and removable.
 * Kept + new photos must then be between minPhotos and maxPhotos.
 * onSubmit(values, photos, keptIds) must return a promise; keptIds are the ids of the existing photos still shown
 * (photos without an id can't be kept by id). The form is disabled while `submitting` is true.
 * guestFields ({ fname, lname, phone }, guest edit link only): adds the guest's name and phone; they come back
 * in values as guest_fname / guest_lname / guest_phone (10 digits).
 */
const VehicleDetailsForm = ({ defaultValues, guestFields, requirePhotos, existingPhotos, minPhotos = 1, maxPhotos = 6, submitting, submitLabel = "SUBMIT VEHICLE", compact = false, onSubmit, onCancel }) => {
  const hasGuest = Boolean(guestFields);
  const [guestFname, setGuestFname] = useState(guestFields?.fname || "");
  const [guestLname, setGuestLname] = useState(guestFields?.lname || "");
  const [guestPhone, setGuestPhone] = useState(formatUsPhone(guestFields?.phone || ""));
  const [license, setLicense] = useState(defaultValues?.license || "");
  const [make, setMake] = useState(defaultValues?.make || "");
  const [model, setModel] = useState(defaultValues?.model || "");
  const [color, setColor] = useState(defaultValues?.color || "");
  const [photos, setPhotos] = useState([]);
  const hasExisting = Array.isArray(existingPhotos);
  const [kept, setKept] = useState(() => (existingPhotos || [])
    .filter((photo) => photo?.url)
    .map((photo, index) => ({ ...photo, key: photo.id !== null && photo.id !== undefined ? `id-${photo.id}` : `url-${index}-${photo.url}` })));
  const totalPhotos = kept.length + photos.length;
  const [photoError, setPhotoError] = useState("");
  const [errors, setErrors] = useState({});
  const summaryRef = useRef(null);
  const [failedSubmits, setFailedSubmits] = useState(0);

  // Bring the error summary into view after a failed submit (runs after the banner has rendered)
  useEffect(() => {
    if (failedSubmits > 0) summaryRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [failedSubmits]);

  // Revoke preview urls only when the form unmounts; removePhoto revokes single ones
  const photosRef = useRef(photos);
  photosRef.current = photos;
  // Latest kept photos for addPhotos, which finishes after an async resize
  const keptRef = useRef(kept);
  keptRef.current = kept;
  const [processing, setProcessing] = useState(false);
  const locked = submitting || processing;
  useEffect(() => () => photosRef.current.forEach((photo) => URL.revokeObjectURL(photo.preview)), []);

  // Camera ("Take photo"), gallery and the add tile all land here; every photo is resized before it's added
  const addPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    setPhotoError("");
    if (files.length === 0) return;

    setProcessing(true);
    const accepted = [];
    let error = "";
    for (const file of files) {
      const ext = file.name && file.name.indexOf(".") !== -1 ? (file.name.split(".").pop() || "").toLowerCase() : "";
      // Some Android camera captures have no extension; the MIME type is enough then
      if (VALID_EXTENSIONS.indexOf(ext) === -1 && VALID_TYPES.indexOf(file.type) === -1) {
        error = "Photos must be JPG or PNG images.";
        continue;
      }
      const compressed = await compressImage(file);
      if (compressed.size > MAX_PHOTO_BYTES) {
        error = "This photo is too large. Please try another photo.";
        continue;
      }
      accepted.push({ file: compressed, preview: URL.createObjectURL(compressed) });
    }
    setProcessing(false);

    // Kept existing photos use up part of the limit
    const maxNew = Math.max(0, maxPhotos - keptRef.current.length);
    let next = photosRef.current.concat(accepted);
    if (next.length > maxNew) {
      error = `You can upload up to ${maxPhotos} photos.`;
      next.slice(maxNew).forEach((photo) => URL.revokeObjectURL(photo.preview));
      next = next.slice(0, maxNew);
    }
    setPhotoError(error);
    setPhotos(next);
    if (keptRef.current.length + next.length >= minPhotos) setErrors((prev) => ({ ...prev, photos: undefined }));
  };

  const removePhoto = (index) => {
    URL.revokeObjectURL(photos[index].preview);
    setPhotos(photos.filter((photo, i) => i !== index));
  };

  const removeExisting = (key) => {
    setKept((prev) => prev.filter((photo) => photo.key !== key));
  };

  const validate = () => {
    const next = {};
    const makeValue = make.trim();
    const preset = COLORS.find((item) => isColor(color, item.id));
    const colorValue = preset ? preset.id : color.trim();

    if (hasGuest) {
      if (guestFname.trim() === "") next.guestFname = "Enter your first name";
      if (guestPhone.replace(/\D/g, "").length !== 10) next.guestPhone = "Enter a valid 10-digit phone number";
    }
    if (license.trim() === "") next.license = "Enter the vehicle number";
    else if (license.trim().length > 20) next.license = "Maximum 20 characters";
    if (makeValue === "") next.make = "Enter the make";
    if (model.trim() === "") next.model = "Enter the model";
    if (colorValue === "") next.color = "Choose or enter your vehicle's color";

    // Updates with current photos: kept + new must reach minPhotos
    const needed = hasExisting || requirePhotos || photos.length > 0 ? minPhotos : 0;
    if (totalPhotos < needed) {
      const missing = needed - totalPhotos;
      next.photos = totalPhotos === 0 ? (minPhotos === 1 ? "Add a photo of your vehicle" : `Add at least ${minPhotos} photos`) : `Add ${missing} more photo${missing > 1 ? "s" : ""}`;
    }
    const values = { license: license.trim(), make: makeValue, model: model.trim(), color: colorValue };
    if (hasGuest) {
      values.guest_fname = guestFname.trim();
      values.guest_lname = guestLname.trim();
      values.guest_phone = guestPhone.replace(/\D/g, "");
    }
    return { next, values };
  };

  const submit = (e) => {
    e.preventDefault();
    const { next, values } = validate();
    const clean = Object.keys(next).reduce((acc, key) => (next[key] ? { ...acc, [key]: next[key] } : acc), {});
    setErrors(clean);
    if (Object.keys(clean).length > 0) {
      setFailedSubmits((n) => n + 1);
      return;
    }
    const keptIds = kept.filter((photo) => photo.id !== null && photo.id !== undefined).map((photo) => photo.id);
    return onSubmit(values, photos.map((photo) => photo.file), keptIds);
  };

  const errorList = Object.values(errors).filter(Boolean);

  return (<form onSubmit={submit} className={compact ? `${styles.form} ${styles.compactForm}` : styles.form} noValidate>
    {errorList.length > 0 && (<div ref={summaryRef} className={styles.errorSummary} role="alert">
      <strong>{errorList.length} field{errorList.length > 1 ? "s need" : " needs"} attention</strong>
      <span>{errorList.join(" · ")}</span>
    </div>)}

    <p className={styles.muted}>All fields marked * are required.</p>

    {hasGuest && (<section className={styles.card}>
      <p className={styles.sectionLabel}>YOUR DETAILS</p>

      <div className={styles.field}>
        <label htmlFor="vd-guest-fname" className={styles.label}>First name*</label>
        <input id="vd-guest-fname" className={`${styles.input} ${errors.guestFname ? styles.inputError : ""}`} value={guestFname} maxLength={50} disabled={submitting} onChange={(e) => setGuestFname(e.target.value)} autoComplete="given-name" />
        {errors.guestFname && <span className={styles.fieldError}>{errors.guestFname}</span>}
      </div>

      <div className={styles.field}>
        <label htmlFor="vd-guest-lname" className={styles.label}>Last name</label>
        <input id="vd-guest-lname" className={styles.input} value={guestLname} maxLength={50} disabled={submitting} onChange={(e) => setGuestLname(e.target.value)} autoComplete="family-name" />
      </div>

      <div className={styles.field}>
        <label htmlFor="vd-guest-phone" className={styles.label}>Mobile phone*</label>
        <input id="vd-guest-phone" type="tel" inputMode="numeric" className={`${styles.input} ${errors.guestPhone ? styles.inputError : ""}`} value={guestPhone} maxLength={14} disabled={submitting} onChange={(e) => setGuestPhone(formatUsPhone(e.target.value))} placeholder="(555) 555-0123" autoComplete="tel-national" />
        <span className={errors.guestPhone ? styles.fieldError : styles.hint}>{errors.guestPhone || "Changes to your phone number apply after the property manager approves them."}</span>
      </div>
    </section>)}

    <section className={styles.card}>
      <p className={styles.sectionLabel}>VEHICLE DETAILS</p>

      <div className={styles.field}>
        <label htmlFor="vd-license" className={styles.label}>Vehicle number*</label>
        <input id="vd-license" className={`${styles.input} ${styles.plateInput} ${errors.license ? styles.inputError : ""}`} value={license} maxLength={20} disabled={submitting} onChange={(e) => setLicense(e.target.value.toUpperCase())} placeholder="ABC 1234" autoComplete="off" />
        <span className={errors.license ? styles.fieldError : styles.hint}>{errors.license || "As shown on your license plate"}</span>
      </div>

      <div className={styles.field}>
        <label htmlFor="vd-make" className={styles.label}>Make*</label>
        <input id="vd-make" className={`${styles.input} ${errors.make ? styles.inputError : ""}`} value={make} maxLength={50} disabled={submitting} onChange={(e) => setMake(e.target.value)} placeholder="e.g. Toyota" />
        {errors.make && <span className={styles.fieldError}>{errors.make}</span>}
      </div>

      <div className={styles.field}>
        <label htmlFor="vd-model" className={styles.label}>Model*</label>
        <input id="vd-model" className={`${styles.input} ${errors.model ? styles.inputError : ""}`} value={model} maxLength={50} disabled={submitting} onChange={(e) => setModel(e.target.value)} placeholder="e.g. Camry" />
        {errors.model && <span className={styles.fieldError}>{errors.model}</span>}
      </div>

      <div className={styles.field}>
        <span id="vd-color-label" className={styles.label}>Color*</span>
        <div role="radiogroup" aria-labelledby="vd-color-label" className={`${styles.colorGrid} ${errors.color ? styles.colorGridError : ""}`}>
          {COLORS.map((item) => (<button
            type="button"
            role="radio"
            aria-checked={isColor(color, item.id)}
            key={item.id}
            className={isColor(color, item.id) ? `${styles.colorTile} ${styles.colorTileOn}` : styles.colorTile}
            disabled={submitting}
            onClick={() => { setColor(item.id); setErrors((prev) => ({ ...prev, color: undefined })); }}
          >
            <span className={styles.colorDot} style={{ background: item.hex }} />
            {item.id}
          </button>))}
        </div>
        <input aria-label="Color" className={`${styles.input} ${errors.color ? styles.inputError : ""}`} value={color} maxLength={30} disabled={submitting} onChange={(e) => setColor(e.target.value)} placeholder="Or type a color, e.g. Dark blue" />
        {errors.color && <span className={styles.fieldError}>{errors.color}</span>}
      </div>
    </section>

    <section className={styles.card}>
      <div className={styles.photoHead}>
        <p className={styles.sectionLabel}>PHOTOS{requirePhotos || hasExisting ? "*" : ""}</p>
        <span className={styles.muted}>{totalPhotos} of {maxPhotos}</span>
      </div>
      <p className={errors.photos ? styles.fieldError : styles.hint}>
        {errors.photos
          ? `${errors.photos}.`
          : hasExisting
            ? `Remove or add photos (at least ${minPhotos}, up to ${maxPhotos}).`
            : requirePhotos
              ? `Add photos of your vehicle (at least ${minPhotos}, up to ${maxPhotos}).`
              : `Optional. New photos replace the current ones (at least ${minPhotos}).`}
      </p>

      <div className={styles.photoGrid}>
        {kept.map((photo, index) => (<div key={photo.key} className={styles.photoThumb}>
          <img src={photo.url} alt={`Current vehicle photo ${index + 1}`} />
          <button type="button" aria-label="Remove photo" onClick={() => removeExisting(photo.key)} disabled={submitting}>×</button>
        </div>))}
        {photos.map((photo, index) => (<div key={photo.preview} className={styles.photoThumb}>
          <img src={photo.preview} alt={`Vehicle photo ${kept.length + index + 1}`} />
          <button type="button" aria-label="Remove photo" onClick={() => removePhoto(index)} disabled={submitting}>×</button>
        </div>))}
        {totalPhotos < maxPhotos && (<label className={styles.photoAdd}>
          <Plus />
          <span>Add photo</span>
          <input type="file" accept="image/jpeg,image/png" multiple onChange={addPhotos} disabled={locked} />
        </label>)}
      </div>

      {totalPhotos < maxPhotos && (<div className={styles.photoButtons}>
        <label className={styles.secondaryButton}>
          <CameraIcon /> Take photo
          <input type="file" accept="image/jpeg,image/png" capture="environment" onChange={addPhotos} disabled={locked} />
        </label>
        <label className={styles.secondaryButton}>
          Gallery
          <input type="file" accept="image/jpeg,image/png" multiple onChange={addPhotos} disabled={locked} />
        </label>
      </div>)}
      {processing
        ? <p className={styles.hint} role="status">Preparing photos...</p>
        : <p className={styles.hint}>JPG or PNG · up to {maxPhotos} photos. Large photos are resized automatically.</p>}
      {photoError !== "" && <p className={styles.fieldError}>{photoError}</p>}
    </section>

    <div className={styles.actions}>
      {onCancel && <button type="button" className={styles.secondaryButton} onClick={onCancel} disabled={submitting}>Cancel</button>}
      <button type="submit" className={styles.primaryButton} disabled={locked}>{submitting ? "PLEASE WAIT..." : submitLabel}</button>
    </div>
  </form>);
};

export default VehicleDetailsForm;
